import { Grid } from "@material-ui/core"
import { LogModel } from "model/api/gestao/clickup/log-model"
import { useMemo, useCallback } from "react"
import { LogVersaoModel } from "model/api/gestao/clickup/log-versao-model"
import { LogLogModel } from "model/api/gestao/clickup/log-log-model"
import { AccordionHistoricoAtualizacoesVersao } from "./components/accordion-historico-atualizacoes/accordion-historico-atualizacoes-versao"
import { CardNaoEncontrado } from "views/components/cards"
import { InformacaoIcon } from "views/components/icons"
import { toDateString } from "utils/to-date"
import { AccordionHistoricoAtualizacoesData } from "./components/accordion-historico-atualizacoes/accordion-historico-atualizacoes-data"

interface HistoricoAtualizacoesListDataProps {
    log: LogModel
    backlogFiltro: "data" | "versao"
    carregando: boolean
    sistemaSelected: string
}

export interface HistoricoVersaoLogsProps {
    versao: LogVersaoModel
    logs: LogLogModel[]
}

export type HistoricoLogVersaoProps = (LogLogModel & { versao: LogVersaoModel })

export interface HistoricoDataLogsProps {
    data: Date
    logs: HistoricoLogVersaoProps[]
}

// Extraída a função para fora do componente para evitar recriação em cada renderização
const getVersionDate = (versao: LogVersaoModel): Date =>
    versao.data_estavel
        ? new Date(versao.data_estavel)
        : versao.data_beta
            ? new Date(versao.data_beta)
            : new Date(versao.data_alfa);

export const HistoricoAtualizacoesListData = ({ log, backlogFiltro, carregando, sistemaSelected }: HistoricoAtualizacoesListDataProps) => {
    // Memoizando o mapeamento de versões e logs associados
    const getVersoes: HistoricoVersaoLogsProps[] = useMemo(() => {
        return log.versao.map(versao => {
            return {
                versao: versao,
                logs: log.log.filter(log => log.atualizacao_id === versao.id)
            }
        })
    }, [log.log, log.versao]);

    const getDatas: HistoricoDataLogsProps[] = useMemo(() => {
        const datas = Array.from(new Set(log.log.map(x => toDateString(x.data) ?? '')))
        const processedDates = () =>
            datas.map(dateStr => ({
                original: dateStr,
                date: (() => {
                    // Converte "DD/MM/YYYY" para Date
                    const [day, month, year] = (dateStr ?? '').split('/');
                    return new Date(Number(year), Number(month) - 1, Number(day));
                })()
            }))
                .sort((a, b) => b.date.getTime() - a.date.getTime())

        const sortedDates = processedDates()
        return sortedDates.map(data => {
            return {
                data: data.date,
                logs: log.log.filter(log => toDateString(log.data) === data.original).map(x => ({
                    ...x,
                    versao: log.versao.find(v => v.id === x.atualizacao_id) || new LogVersaoModel()
                }))
            }
        })
    }, [log.log, log.versao])

    // Função de comparação memoizada para evitar recriação
    const compareVersions = useCallback((a: HistoricoVersaoLogsProps, b: HistoricoVersaoLogsProps): number => {
        if (backlogFiltro === "versao") {
            if (['60', '61'].includes(String(sistemaSelected))) {
                return Number(b.versao.versao) - Number(a.versao.versao);
            }
            return String(b.versao.versao).localeCompare(String(a.versao.versao));
        } else {
            return getVersionDate(b.versao).getTime() - getVersionDate(a.versao).getTime();
        }
    }, [backlogFiltro, sistemaSelected]);

    // Memoizando a operação completa de filtro, ordenação e mapeamento
    const processedMap = useMemo(() => {
        if (!carregando && getVersoes.filter(x => x.logs.length > 0).length === 0) return (
            <Grid item xs={12}>
                <CardNaoEncontrado
                    icon={<InformacaoIcon />}
                    mensagem="Nenhuma atualização encontrada"
                />
            </Grid>
        )
        if (backlogFiltro === "data") {
            return getDatas
                .filter(x => x.logs.length > 0)
                .sort((a, b) => b.data.getTime() - a.data.getTime())
                .map((back, i) => (
                    <Grid item xs={12} key={i}>
                        <AccordionHistoricoAtualizacoesData backlog={back} index={i} />
                    </Grid>
                ))
        }
        return getVersoes
            .filter(x => x.logs.length > 0)
            .sort(compareVersions)
            .map((back, i) => (
                <Grid item xs={12} key={back.versao.id}>
                    <AccordionHistoricoAtualizacoesVersao backlog={back} index={i} />
                </Grid>
            ));
    }, [carregando, getVersoes, backlogFiltro, compareVersions, getDatas]);

    return (
        <Grid container>
            {processedMap}
        </Grid>
    )
}